/**
 * Where the console keeps its refresh token: one slot in `localStorage`, shared by every tab.
 *
 * Refresh tokens rotate, and presenting one that was already used revokes the whole sign-in
 * — the server's answer to theft. A browser is several holders of one token: the tab a
 * dispatcher reloads, and the second tab with the map in it. So the slot is written on every
 * rotation rather than only at sign-in, and a refresh runs under a lock and presents what the
 * slot holds, never the copy a tab remembers. Before this, the slot kept the token from sign-in
 * while the tab rotated past it every fifteen minutes; a reload presented a used token, and
 * the session ended in every tab at once.
 *
 * The slot names the sign-in it belongs to. Somebody else signing in in another tab replaces
 * it, and a tab of the earlier sign-in must neither read that token — it would quietly become
 * that person — nor write over it. A tab that finds no slot of its own at its next refresh has
 * no session left in this browser: signed out in another tab, or taken over by another
 * sign-in. The client ends it there, without asking the server — the only token the tab has
 * is a used one, and presenting it is what the server logs as theft.
 *
 * It does not wait for that refresh, which can be fifteen minutes away. The browser tells
 * every other tab when the slot changes, and `lost` is how a tab reads that news: signing
 * out in one tab signs out all of them at once, the way every other system does.
 */

import type { TokenStore } from '@taxi/api-client';

const KEY = 'taxi.session';

/**
 * Where builds before this one kept the bare token. Removed on sight rather than carried
 * over: it was a used token more often than not, and presenting it ends the session.
 */
const LEGACY_KEY = 'taxi.refresh';

const LOCK = 'taxi.session.refresh';

interface Slot {
  session: string;
  token: string;
}

/** As much of the browser's `LockManager` as taking turns needs. */
export interface Locks {
  request<T>(name: string, work: () => Promise<T>): Promise<T>;
}

export interface SessionStore extends TokenStore {
  /** A new sign-in in this tab. The slot becomes this tab's, whoever had it. */
  begin(): void;

  /** On load: carry on whichever sign-in the slot holds. True when it holds one. */
  adopt(): boolean;

  /** Lets go of the slot without the server hearing about it — it belongs to another firm. */
  forget(): void;

  /**
   * Whether a change another tab made — to `changedKey`, or to everything when it is null,
   * as a `storage` event reports it — has left this tab without its sign-in. A rotation by
   * a tab of the same sign-in has not; a sign-out, or somebody else signing in, has.
   */
  lost(changedKey: string | null): boolean;
}

/**
 * Both arguments are asked for when needed rather than at creation, so importing this under
 * Node — which has no `localStorage` — costs nothing.
 *
 * `locks` is absent outside a secure context. Tabs then refresh without taking turns, which
 * is the old behaviour, and the reason every real deployment is served over TLS anyway.
 */
export function createSessionStore(
  storage: () => Storage,
  locks: () => Locks | undefined,
): SessionStore {
  let mine: string | null = null;

  function slot(): Slot | null {
    const raw = storage().getItem(KEY);

    if (raw === null) return null;

    try {
      const parsed = JSON.parse(raw) as Partial<Slot>;

      return typeof parsed.session === 'string' && typeof parsed.token === 'string'
        ? { session: parsed.session, token: parsed.token }
        : null;
    } catch {
      return null;
    }
  }

  function ours(): Slot | null {
    const current = slot();

    return current !== null && current.session === mine ? current : null;
  }

  return {
    read: () => Promise.resolve(ours()?.token ?? null),

    write(token) {
      const current = slot();

      if (token === null) {
        if (current !== null && current.session === mine) storage().removeItem(KEY);
      } else if (mine !== null && (current === null || current.session === mine)) {
        storage().setItem(KEY, JSON.stringify({ session: mine, token } satisfies Slot));
      }

      return Promise.resolve();
    },

    exclusive(work) {
      const manager = locks();

      return manager === undefined ? work() : manager.request(LOCK, () => work());
    },

    begin() {
      mine = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      storage().removeItem(KEY);
      storage().removeItem(LEGACY_KEY);
    },

    adopt() {
      storage().removeItem(LEGACY_KEY);
      mine = slot()?.session ?? null;

      return mine !== null;
    },

    forget() {
      if (ours() !== null) storage().removeItem(KEY);
      mine = null;
    },

    lost(changedKey) {
      if (changedKey !== null && changedKey !== KEY) return false;

      return mine !== null && ours() === null;
    },
  };
}

export const sessionStore = createSessionStore(
  () => localStorage,
  // Typed as always present; outside a secure context it is not.
  () => (navigator as { locks?: Locks }).locks,
);
